import styled, { keyframes, css } from 'styled-components';

/* Page-local aliases over the global --if-* layer (src/theme/tokens.css). */
const tokens = css`
  --bg: var(--if-bg);
  --surface: var(--if-surface-1);
  --surface-2: var(--if-surface-2);
  --surface-hover: var(--if-surface-2);
  --border: var(--if-border);
  --border-strong: var(--if-border-strong);
  --ink: var(--if-ink-1);
  --ink-2: var(--if-ink-1);
  --muted: var(--if-ink-2);
  --accent: var(--if-accent);
  --accent-soft: var(--if-accent-soft);
  --good: var(--if-success-ink);
  --good-bright: var(--if-success);
  --good-soft: var(--if-success-soft);
  --warn: var(--if-warning-ink);
  --warn-bright: var(--if-warning);
  --warn-soft: var(--if-warning-soft);
  --danger: var(--if-danger-ink);
  --danger-bright: var(--if-danger);
  --danger-soft: var(--if-danger-soft);
  --info: var(--if-info-ink);
  --info-bright: var(--if-info);
  --info-soft: var(--if-info-soft);
  --idle: var(--if-ink-2);
  --idle-bright: var(--if-ink-3);
  --idle-soft: var(--if-surface-3);
  --shadow: 0 1px 2px color-mix(in srgb, var(--if-shadow) 10%, transparent),
    0 6px 18px -10px color-mix(in srgb, var(--if-shadow) 40%, transparent);
  --shadow-sm: 0 1px 2px color-mix(in srgb, var(--if-shadow) 15%, transparent);
`;
const mono =
  "'Roboto Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

export const pulseDot = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const pulseText = keyframes`0%,100%{opacity:1}50%{opacity:.55}`;
const shimmer = keyframes`0%{background-position:100% 0}100%{background-position:-100% 0}`;

export const Page = styled.div`
  ${tokens}
  width: 100%;
  min-height: calc(100vh - 60px);
  background: var(--bg);
  color: var(--ink);
  font-size: 14px;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--muted);
  white-space: nowrap;
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--good-bright);
  }
`;
export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font: 600 12px inherit;
  color: var(--muted);
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;
export const Select = styled.select`
  cursor: pointer;
  font: 600 12px inherit;
  color: var(--muted);
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;
export const Updated = styled.span`
  font-size: 12px;
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  .spin {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--good-bright);
  }
`;

export const Body = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px 48px;
`;

export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  @media (max-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 620px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
export const Tile = styled.button`
  text-align: left;
  cursor: pointer;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 15px 16px;
  box-shadow: var(--shadow-sm);
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.12s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow);
  }
  &[aria-pressed='true'] {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .n {
    font: 750 30px/1 inherit;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
  }
  .l {
    font-size: 12.5px;
    color: var(--muted);
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 7px;
    font-weight: 600;
  }
  .sw {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    background: var(--border-strong);
  }
  &.healthy .n {
    color: var(--good);
  }
  &.healthy .sw {
    background: var(--good-bright);
  }
  &.degraded .n {
    color: var(--warn);
  }
  &.degraded .sw {
    background: var(--warn-bright);
  }
  &.unhealthy .n {
    color: var(--danger);
  }
  &.unhealthy .sw {
    background: var(--danger-bright);
  }
  &.idle .n {
    color: var(--idle);
  }
  &.idle .sw {
    background: var(--idle-bright);
  }
  &.unhealthy.alerting {
    border-color: color-mix(in srgb, var(--danger-bright) 55%, var(--border));
  }
  &.unhealthy.alerting .n {
    animation: ${pulseText} 1.9s ease-in-out infinite;
  }
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;
export const Segmented = styled.div`
  display: inline-flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px;
`;
export const SegButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font: 650 12.5px inherit;
  color: var(--muted);
  padding: 7px 15px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  &[aria-pressed='true'] {
    background: var(--accent);
    color: var(--if-on-accent);
  }
  &[aria-pressed='true'].unhealthy {
    background: var(--danger-bright);
  }
  &[aria-pressed='true'].degraded {
    background: var(--warn-bright);
  }
  &[aria-pressed='true'].healthy {
    background: var(--good-bright);
  }
  &[aria-pressed='true'].idle {
    background: var(--idle-bright);
  }
  .count {
    font: 600 11px ${mono};
    opacity: 0.85;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;
export const Search = styled.input`
  border: 1px solid var(--border-strong);
  background: var(--surface);
  border-radius: 8px;
  padding: 8px 12px;
  font: 400 13px inherit;
  color: var(--ink);
  min-width: 210px;
  &::placeholder {
    color: var(--muted);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

export const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow);
  overflow: hidden;
`;
export const Scroll = styled.div`
  overflow-x: auto;
`;
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
  thead th {
    text-align: left;
    font: 650 11px ${mono};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted);
    padding: 11px 16px;
    background: var(--surface-2);
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  tbody td {
    padding: 13px 16px;
    border-top: 1px solid var(--border);
    vertical-align: middle;
  }
`;
export const Row = styled.tr`
  & > td:first-child {
    border-left: 3px solid transparent;
  }
  &.sev-unhealthy > td:first-child {
    border-left-color: var(--danger-bright);
  }
  &.sev-degraded > td:first-child {
    border-left-color: var(--warn-bright);
  }
  &.sev-idle > td:first-child {
    border-left-color: var(--idle-bright);
  }
  &.expandable {
    cursor: pointer;
  }
  &.expandable:hover {
    background: var(--surface-hover);
  }
`;
export const IntgName = styled.div`
  font-weight: 660;
  color: var(--ink);
  display: flex;
  align-items: center;
  gap: 8px;
  a {
    color: var(--ink);
    text-decoration: none;
  }
  a:hover {
    color: var(--accent);
    text-decoration: underline;
  }
  .ext {
    color: var(--muted);
    opacity: 0;
    transition: opacity 0.12s;
    font-size: 12px;
  }
  tr:hover & .ext {
    opacity: 1;
    color: var(--accent);
  }
  .sub {
    display: block;
    font: 500 11.5px inherit;
    color: var(--muted);
    margin-top: 2px;
  }
`;
export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 650 11.5px inherit;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  .d {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
  &.unhealthy {
    background: var(--danger-soft);
    color: var(--danger);
  }
  &.unhealthy .d {
    background: var(--danger-bright);
    animation: ${pulseDot} 1.7s ease-in-out infinite;
  }
  &.degraded {
    background: var(--warn-soft);
    color: var(--warn);
  }
  &.degraded .d {
    background: var(--warn-bright);
  }
  &.healthy {
    background: var(--good-soft);
    color: var(--good);
  }
  &.healthy .d {
    background: var(--good-bright);
  }
  &.idle {
    background: var(--idle-soft);
    color: var(--idle);
  }
  &.idle .d {
    background: var(--idle-bright);
  }
`;
export const StatusTags = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font: 650 12px ${mono};
  font-variant-numeric: tabular-nums;
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  }
  span i {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex: none;
  }
  &.empty {
    color: var(--muted);
    font-weight: 500;
  }
`;
export const LastOk = styled.span`
  font: 500 12.5px ${mono};
  color: var(--muted);
  white-space: nowrap;
  &.stale {
    color: var(--danger);
  }
`;
export const Reason = styled.span`
  font-size: 12.5px;
  color: var(--ink-2);
  max-width: 320px;
  display: inline-block;
`;
export const Chev = styled.span`
  color: var(--muted);
  transition: transform 0.15s;
  display: inline-block;
  &.open {
    transform: rotate(90deg);
  }
`;
export const DetailPad = styled.div`
  padding: 14px 4px 16px 20px;
  background: var(--surface-2);
  h4 {
    margin: 0 0 8px;
    font: 650 11px ${mono};
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--accent);
  }
`;
export const ExRow = styled.div`
  display: grid;
  grid-template-columns: 160px 130px 1fr;
  gap: 12px;
  font: 500 12px ${mono};
  color: var(--ink-2);
  padding: 5px 0;
  border-top: 1px dashed var(--border);
  align-items: start;
  &:first-of-type {
    border-top: 0;
  }
  .st {
    font-weight: 700;
  }
  .detail {
    min-width: 0;
    overflow-wrap: anywhere;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .muted {
    color: var(--muted);
  }
`;
export const Skeleton = styled.div`
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--surface-2) 25%,
    var(--surface-hover) 37%,
    var(--surface-2) 63%
  );
  background-size: 400% 100%;
  animation: ${shimmer} 1.3s ease infinite;
`;
export const Empty = styled.div`
  padding: 56px 20px;
  text-align: center;
  .mark {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--good-soft);
    color: var(--good);
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    font-size: 26px;
  }
  h3 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 700;
    color: var(--ink);
  }
  p {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
`;
export const Legend = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 16px;
  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;
export const LegendCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
  .h {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
  }
  .d {
    font-size: 12px;
    color: var(--muted);
  }
  .d b {
    color: var(--ink-2);
  }
`;
export const Foot = styled.div`
  margin-top: 16px;
  color: var(--muted);
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  code {
    font-family: ${mono};
  }
`;

/* ---- view switcher ---- */
export const ViewSwitch = styled.div`
  display: inline-flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px;
`;
export const ViewButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font: 650 12.5px inherit;
  color: var(--muted);
  padding: 7px 13px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &[aria-pressed='true'] {
    background: var(--accent);
    color: var(--if-on-accent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

/* ---- focus cards ---- */
export const AttnHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 0 12px;
  font-weight: 700;
  font-size: 15px;
  color: var(--ink);
  .badge {
    font: 640 11px ${mono};
    background: var(--danger-soft);
    color: var(--danger);
    padding: 2px 8px;
    border-radius: 999px;
  }
`;
export const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
`;
export const FCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-top: 3px solid var(--border);
  border-radius: 14px;
  padding: 16px;
  box-shadow: var(--shadow);
  &.unhealthy {
    border-top-color: var(--danger-bright);
  }
  &.degraded {
    border-top-color: var(--warn-bright);
  }
  &.idle {
    border-top-color: var(--idle-bright);
  }
  .top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
  }
  .nm {
    font-weight: 680;
    font-size: 14.5px;
    color: var(--ink);
  }
  .sub {
    font: 500 11.5px inherit;
    color: var(--muted);
    margin-top: 2px;
  }
  .frs {
    font-size: 12.5px;
    color: var(--ink-2);
    min-height: 34px;
  }
`;
export const Bar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 5px;
  overflow: hidden;
  margin: 14px 0 8px;
  background: var(--surface-2);
  i {
    display: block;
  }
  .ok {
    background: var(--good-bright);
  }
  .bad {
    background: var(--danger-bright);
  }
  .blk {
    background: var(--warn-bright);
  }
`;
export const FStats = styled.div`
  display: flex;
  justify-content: space-between;
  font: 600 12px ${mono};
  color: var(--muted);
  margin-bottom: 10px;
  b {
    color: var(--ink);
  }
`;
export const FActions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border);
  padding-top: 11px;
`;
export const CardLink = styled.a`
  color: var(--accent);
  font-weight: 640;
  font-size: 12.5px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
export const HealthyStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--good-soft);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 16px;
  color: var(--good);
  font-weight: 600;
  flex-wrap: wrap;
  .chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .hchip {
    font: 500 11.5px inherit;
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--ink-2);
    padding: 3px 8px;
    border-radius: 999px;
  }
`;

/* ---- map heatmap ---- */
export const HeatLegend = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: var(--muted);
  align-items: center;
  margin-bottom: 12px;
  i {
    width: 11px;
    height: 11px;
    border-radius: 3px;
    display: inline-block;
    margin-right: 5px;
  }
`;
export const HeatLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 16px;
  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;
export const HeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
  align-content: start;
`;
export const Cell = styled.button`
  text-align: left;
  aspect-ratio: 1.35;
  border-radius: 9px;
  border: 1px solid var(--border);
  padding: 9px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: var(--surface);
  transition: transform 0.1s, box-shadow 0.1s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow);
  }
  &.sel {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &.unhealthy {
    background: var(--danger-soft);
  }
  &.degraded {
    background: var(--warn-soft);
  }
  &.idle {
    background: var(--idle-soft);
  }
  .nm {
    font: 600 11px inherit;
    line-height: 1.25;
    color: var(--ink);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  &.unhealthy .dot {
    background: var(--danger-bright);
    animation: ${pulseDot} 1.7s ease-in-out infinite;
  }
  &.degraded .dot {
    background: var(--warn-bright);
  }
  &.healthy .dot {
    background: var(--good-bright);
  }
  &.idle .dot {
    background: var(--idle-bright);
  }
  .mini {
    font: 600 10.5px ${mono};
    color: var(--muted);
  }
`;
export const HeatDetail = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--shadow-sm);
  align-self: start;
  h4 {
    margin: 0 0 3px;
    font-size: 15px;
    color: var(--ink);
  }
  .sub {
    color: var(--muted);
    font-size: 12px;
    margin-bottom: 12px;
  }
`;
export const DRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  padding: 7px 0;
  border-top: 1px solid var(--border);
  span:last-child {
    font-family: ${mono};
    color: var(--ink-2);
    text-align: right;
  }
`;
